import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { TbClock, TbX } from "react-icons/tb";

interface BmsTimePickerProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  allowClear?: boolean;
  minuteStep?: number;
  minHour?: number;
  maxHour?: number;
  disabled?: boolean;
  className?: string;
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function parseTime(v: string): { h: number | null; m: number | null } {
  if (!v) return { h: null, m: null };
  const [hh, mm] = v.split(":").map((s) => parseInt(s, 10));
  return {
    h: Number.isFinite(hh) ? hh : null,
    m: Number.isFinite(mm) ? mm : null,
  };
}

const POPOVER_WIDTH = 200;
const POPOVER_HEIGHT = 280; // approx; list is 210 + header + footer

const BmsTimePicker: React.FC<BmsTimePickerProps> = ({
  value,
  onChange,
  placeholder = "--:--",
  allowClear = false,
  minuteStep = 5,
  minHour = 0,
  maxHour = 23,
  disabled = false,
  className = "",
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const hourListRef = useRef<HTMLDivElement>(null);
  const minuteListRef = useRef<HTMLDivElement>(null);

  const [pos, setPos] = useState<{ top: number; left: number; placement: "bottom" | "top" }>({
    top: 0, left: 0, placement: "bottom",
  });

  const { h, m } = useMemo(() => parseTime(value), [value]);

  const hourList = useMemo(() => {
    const out: number[] = [];
    for (let i = minHour; i <= maxHour; i++) out.push(i);
    return out;
  }, [minHour, maxHour]);

  const minuteList = useMemo(() => {
    const out: number[] = [];
    for (let i = 0; i < 60; i += Math.max(1, minuteStep)) out.push(i);
    return out;
  }, [minuteStep]);

  const recomputePosition = useCallback(() => {
    const input = rootRef.current;
    if (!input) return;
    const rect = input.getBoundingClientRect();
    const vpH = window.innerHeight;
    const vpW = window.innerWidth;

    const spaceBelow = vpH - rect.bottom;
    const spaceAbove = rect.top;
    const placement: "bottom" | "top" =
      spaceBelow >= POPOVER_HEIGHT + 12 || spaceBelow >= spaceAbove ? "bottom" : "top";

    const width = Math.max(POPOVER_WIDTH, rect.width);
    let left = rect.left;
    if (left + width > vpW - 8) left = Math.max(8, vpW - width - 8);
    if (left < 8) left = 8;

    const top =
      placement === "bottom" ? rect.bottom + 4 : Math.max(8, rect.top - POPOVER_HEIGHT - 4);

    setPos({ top, left, placement });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    recomputePosition();
  }, [open, recomputePosition]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => recomputePosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, recomputePosition]);

  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const scrollSelected = (container: HTMLDivElement | null) => {
      const el = container?.querySelector(".bms-tp-opt.selected") as HTMLElement | null;
      if (el && container) {
        container.scrollTop = el.offsetTop - container.clientHeight / 2 + el.clientHeight / 2;
      }
    };
    requestAnimationFrame(() => {
      scrollSelected(hourListRef.current);
      scrollSelected(minuteListRef.current);
    });
  }, [open]);

  const commit = (nh: number | null, nm: number | null) => {
    if (nh === null || nm === null) return;
    onChange(`${pad2(nh)}:${pad2(nm)}`);
  };

  const setHour = (nh: number) => commit(nh, m ?? 0);
  const setMinute = (nm: number) => commit(h ?? 9, nm);

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
  };

  const display = value
    ? value
    : <span className="bms-tp-placeholder">{placeholder}</span>;

  const popover = open ? (
    <div
      ref={popoverRef}
      className={`bms-tp-popover bms-tp-popover-portal placement-${pos.placement}`}
      style={{ top: pos.top, left: pos.left, width: POPOVER_WIDTH }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="bms-tp-cols">
        <div className="bms-tp-col">
          <div className="bms-tp-col-label">{t("bms_tp_hour")}</div>
          <div ref={hourListRef} className="bms-tp-col-list" role="listbox">
            {hourList.map((hh) => (
              <button
                key={hh}
                type="button"
                className={`bms-tp-opt ${h === hh ? "selected" : ""}`}
                onClick={() => setHour(hh)}
              >
                {pad2(hh)}
              </button>
            ))}
          </div>
        </div>
        <div className="bms-tp-col">
          <div className="bms-tp-col-label">{t("bms_tp_minute")}</div>
          <div ref={minuteListRef} className="bms-tp-col-list" role="listbox">
            {minuteList.map((mm) => (
              <button
                key={mm}
                type="button"
                className={`bms-tp-opt ${m === mm ? "selected" : ""}`}
                onClick={() => setMinute(mm)}
              >
                {pad2(mm)}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="bms-tp-footer">
        <button
          type="button"
          className="bms-tp-done"
          onClick={() => setOpen(false)}
        >
          {t("bms_tp_done")}
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div
      ref={rootRef}
      className={`bms-time-picker ${open ? "open" : ""} ${disabled ? "disabled" : ""} ${className}`}
    >
      <div
        className="bms-tp-input"
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
        aria-disabled={disabled}
      >
        <TbClock className="bms-tp-icon" />
        <span className="bms-tp-value">{display}</span>
        {allowClear && value && !disabled && (
          <button
            type="button"
            className="bms-tp-clear"
            onClick={handleClear}
            aria-label="Clear"
          >
            <TbX size={14} />
          </button>
        )}
      </div>

      {popover && ReactDOM.createPortal(popover, document.body)}
    </div>
  );
};

export default BmsTimePicker;
